import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { SnackbarService } from '@ccfx/shared-lib';
import { throwError } from 'rxjs';

const MAX_PAYLOAD_VALUE = 9_999_999_999.99;
const METHODS_WITH_BODY = ['POST', 'PUT', 'PATCH'];

function hasExceedingValue(value: unknown): boolean {
  if (typeof value === 'number') {
    return value > MAX_PAYLOAD_VALUE;
  }
  if (Array.isArray(value)) {
    return value.some(hasExceedingValue);
  }
  if (value !== null && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some(hasExceedingValue);
  }
  return false;
}

/**
 * Blocks outbound API requests where any numeric value in the body exceeds
 * the maximum allowed amount of 9,999,999,999.99.
 * Applies to POST, PUT, and PATCH requests. Skips FormData (file uploads).
 */
export const payloadValueLimitInterceptor: HttpInterceptorFn = (req, next) => {
  const snackbarService = inject(SnackbarService);

  if (!METHODS_WITH_BODY.includes(req.method)) {
    return next(req);
  }

  if (req.body instanceof FormData) {
    return next(req);
  }

  if (req.body != null && hasExceedingValue(req.body)) {
    snackbarService.error(
      'A value in the request exceeds the maximum allowed amount of 9,999,999,999.99.',
    );
    return throwError(
      () => new Error('Payload value exceeds the maximum allowed amount of 9,999,999,999.99.'),
    );
  }

  return next(req);
};
